import React from 'react';
import { motion } from 'motion/react';
import { MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const BRANCHES = [
  {
    city: 'Hyderabad',
    address: 'Puppalaguda Main Rd, near YSR Statue, Narsingi, Hyderabad, Telangana 500089',
    phone: '9989697942',
    map: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3808.178!2d78.3573!3d17.3846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDIzJzA0LjYiTiA3OMKwMjEnMjYuMyJF!5e0!3m2!1sen!2sin!4v1680000000000',
    slug: '/interior-designers-in-hyderabad',
  },
  {
    city: 'Vijayawada',
    address: 'Vijayalakshmi Enclave, Nidamanuru, Vijayawada, 521104',
    phone: '9989697942',
    map: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.123!2d80.6480!3d16.5062!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTbCsDMwJzIyLjMiTiA4MMKwMzgnNTIuOCJF!5e0!3m2!1sen!2sin!4v1680000000000',
    slug: '/interior-designers-in-vijayawada',
  },
];

export function BranchesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="contact" className={`py-12 sm:py-24 transition-colors duration-500 ${isDark ? 'bg-[#111827]' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`text-2xl sm:text-3xl lg:text-[42px] font-poppins font-bold leading-tight mb-4 ${isDark ? 'text-white' : 'text-primary'}`}
          >
            Find Us{' '}
            <span className="text-accent">Near You</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={isDark ? 'text-gray-400 text-base sm:text-lg max-w-2xl mx-auto' : 'text-gray-500 text-base sm:text-lg max-w-2xl mx-auto'}
          >
            Visit our design studios for a walkthrough of materials and finishes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`mt-8 p-5 sm:p-6 rounded-2xl max-w-3xl mx-auto border text-center ${isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-blue-50/50 border-blue-100'}`}
          >
            <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              We currently operate in major cities across <strong className={isDark ? 'text-white' : 'text-primary'}>Telangana and Andhra Pradesh</strong> with full design studios and manufacturing hubs in these states. We also serve clients in cities across <strong className={isDark ? 'text-white' : 'text-primary'}>Karnataka and Tamil Nadu</strong>.
            </p>
          </motion.div>
        </div>

        {/* Branch Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {BRANCHES.map((branch, i) => (
            <motion.div
              key={branch.city}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className={`rounded-2xl overflow-hidden shadow-sm border group hover:shadow-lg transition-shadow duration-300 ${isDark ? 'bg-[#1E293B] border-gray-700' : 'bg-bg-light border-gray-100'}`}
            >
              {/* Map */}
              <div className="h-[220px] w-full overflow-hidden">
                <iframe
                  src={branch.map}
                  title={`Aura Spaces ${branch.city}`}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full border-0"
                />
              </div>
              {/* Info */}
              <div className="p-6">
                <h3 className={`font-poppins font-bold text-xl mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-primary'}`}>
                  <MapPin className="w-5 h-5 text-accent" />
                  {branch.city}
                </h3>
                <p className={`text-sm leading-relaxed mb-4 min-h-[48px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{branch.address}</p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={branch.slug}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-white font-semibold text-sm hover:bg-accent-hover transition-all duration-300 shadow-md shadow-accent/10"
                  >
                    Explore Studio →
                  </Link>
                  <a
                    href={`tel:${branch.phone}`}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 border ${
                      isDark 
                        ? 'border-gray-600 bg-white/5 text-gray-300 hover:bg-white/10' 
                        : 'border-gray-200 bg-white text-gray-650 hover:bg-gray-50'
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    Call Studio
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
